import React, { useMemo } from "react";
import type {
  CampaignCharacter,
  PremiumCharacterRule,
  PremiumCriteria,
  PromptTemplate,
  SendSchedule,
  SendTimeOption,
} from "../types";
import { DEFAULT_SEND_TIME_VALUE } from "../types";
import { SectionCard } from "./SectionLayout";
import { EmailBodyEditor } from "./EmailBodyEditor";

interface PremiumConfigurationSectionProps {
  criteria: PremiumCriteria;
  prompt: PromptTemplate;
  characters: CampaignCharacter[];
  onUpdate: (criteria: PremiumCriteria) => void;
  onUpdatePrompt: (prompt: PromptTemplate) => void;
  sendTimeOptions: SendTimeOption[];
  onSave: () => void;
  isSaving?: boolean;
}

interface ReachoutRulesEditorProps {
  rules: PremiumCharacterRule[];
  onChange: (rules: PremiumCharacterRule[]) => void;
  characters: CampaignCharacter[];
  sendTimeOptions: SendTimeOption[];
}

const generateRuleId = () =>
  `reachout-rule-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;

const getDefaultWindow = (options: SendTimeOption[]): SendSchedule => ({
  offsetValue: 12,
  offsetUnit: "hours",
  sendTimeOptionId: options[0]?.value ?? DEFAULT_SEND_TIME_VALUE,
});

const OFFSET_UNITS: SendSchedule["offsetUnit"][] = [
  "minutes",
  "hours",
  "days",
  "weeks",
  "months",
];

const ReachoutRulesEditor: React.FC<ReachoutRulesEditorProps> = ({
  rules,
  onChange,
  characters,
  sendTimeOptions,
}) => {
  const hasCharacters = characters.length > 0;

  const ensureReachoutCharacter = () => characters[0]?.id ?? "";

  const addRule = () => {
    const newRule: PremiumCharacterRule = {
      id: generateRuleId(),
      reachoutCharacterId: ensureReachoutCharacter(),
      reachoutCharacterTier: "",
      triggerCharacterIds: [],
      minimumTokens: 0,
      triggerAfterAnyCount: undefined,
      resendCooldownDays: 7,
      sendWindow: getDefaultWindow(sendTimeOptions),
      promptInstructions: "",
      manualEmailEnabled: false,
      manualEmailSubject: "",
      manualEmailBody: "",
    };
    onChange([...rules, newRule]);
  };

  const removeRule = (ruleId: string) => {
    onChange(rules.filter((rule) => rule.id !== ruleId));
  };

  const updateRule = (
    ruleId: string,
    updater: (rule: PremiumCharacterRule) => PremiumCharacterRule
  ) => {
    onChange(
      rules.map((rule) => (rule.id === ruleId ? updater(rule) : rule))
    );
  };

  const toggleTriggerCharacter = (ruleId: string, characterId: string) => {
    updateRule(ruleId, (current) => {
      const isAlreadySelected = current.triggerCharacterIds.includes(
        characterId
      );
      const nextIds = isAlreadySelected
        ? current.triggerCharacterIds.filter((id) => id !== characterId)
        : [...current.triggerCharacterIds, characterId];
      return {
        ...current,
        triggerCharacterIds: nextIds,
        triggerAfterAnyCount:
          nextIds.length > 0 ? undefined : current.triggerAfterAnyCount,
      };
    });
  };

  const setRuleContentMode = (ruleId: string, mode: "prompt" | "manual") => {
    updateRule(ruleId, (current) => ({
      ...current,
      manualEmailEnabled: mode === "manual",
    }));
  };

  const handleReachoutChange = (ruleId: string, characterId: string) => {
    updateRule(ruleId, (current) => ({
      ...current,
      reachoutCharacterId: characterId,
    }));
  };

  const updateSendWindow = (
    ruleId: string,
    updates: Partial<SendSchedule>
  ) => {
    updateRule(ruleId, (current) => {
      const nextWindow =
        current.sendWindow ?? getDefaultWindow(sendTimeOptions);
      return {
        ...current,
        sendWindow: { ...nextWindow, ...updates },
      };
    });
  };

  const updateTriggerAfterAnyCount = (ruleId: string, rawValue: string) => {
    if (rawValue === "") {
      updateRule(ruleId, (current) => ({
        ...current,
        triggerAfterAnyCount: undefined,
      }));
      return;
    }

    const parsedValue = Number(rawValue);
    const sanitizedValue = Number.isFinite(parsedValue)
      ? Math.max(1, Math.floor(parsedValue))
      : 1;

    updateRule(ruleId, (current) => ({
      ...current,
      triggerAfterAnyCount: sanitizedValue,
      triggerCharacterIds: [],
    }));
  };

  const handleManualTriggerEntry = (ruleId: string, rawValue: string) => {
    const ids = rawValue
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    updateRule(ruleId, (current) => ({
      ...current,
      triggerCharacterIds: ids,
      triggerAfterAnyCount:
        ids.length > 0 ? undefined : current.triggerAfterAnyCount,
    }));
  };

  return (
    <div className="space-y-4">
      {!hasCharacters ? (
        <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No characters are loaded yet.
        </div>
      ) : null}
      {rules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
          No reach-out rules yet. Add one to map chat combinations to the
          character who should email them.
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule) => {
            const selectedReachout = hasCharacters
              ? characters.find(
                  (character) => character.id === rule.reachoutCharacterId
                )
              : undefined;
            const readableTriggers = rule.triggerCharacterIds
              .map(
                (characterId) =>
                  characters.find((character) => character.id === characterId)
                    ?.name ?? characterId
              )
              .join(", ");
            const resolvedWindow =
              rule.sendWindow ?? getDefaultWindow(sendTimeOptions);
            const hasSpecificTriggerCharacters =
              rule.triggerCharacterIds.length > 0;
            const hasCharacterCountRule =
              typeof rule.triggerAfterAnyCount === "number" &&
              rule.triggerAfterAnyCount > 0;
            let triggerSummary =
              "Select characters or set a minimum number to define this rule.";

            if (hasSpecificTriggerCharacters) {
              triggerSummary = `Triggers when they've chatted with: ${readableTriggers}.`;
            } else if (hasCharacterCountRule) {
              triggerSummary = `Triggers once they've chatted with any ${rule.triggerAfterAnyCount}+ characters overall.`;
            }

            const triggerAfterAnyCountValue =
              rule.triggerAfterAnyCount?.toString() ?? "";
            const activeContentMode = rule.manualEmailEnabled ? "manual" : "prompt";

          return (
              <div
                key={rule.id}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="space-y-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-2 md:w-1/2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        Reach-out character
                      </span>
                      <div className="relative w-full">
                        {hasCharacters ? (
                          <>
                            <select
                              value={rule.reachoutCharacterId}
                              onChange={(event) =>
                                handleReachoutChange(rule.id, event.target.value)
                              }
                              className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-12 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                            >
                              {characters.map((character) => (
                                <option key={character.id} value={character.id}>
                                  {character.name}
                                </option>
                              ))}
                            </select>
                            <svg
                              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                              viewBox="0 0 20 20"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M6 8l4 4 4-4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </>
                        ) : (
                          <input
                            type="text"
                            value={rule.reachoutCharacterId}
                            onChange={(event) =>
                              handleReachoutChange(rule.id, event.target.value)
                            }
                            placeholder="Enter character ID"
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                          />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        Any character can carry the email — mix free, patron, or
                        premium voices.
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:w-1/2">
                      <label className="flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Minimum total tokens
                        </span>
                        <input
                          type="number"
                          min={0}
                          step={1000}
                          value={rule.minimumTokens}
                          onChange={(event) =>
                            updateRule(rule.id, (current) => ({
                              ...current,
                              minimumTokens: Number(event.target.value),
                            }))
                          }
                          className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                        />
                        <span className="text-xs text-gray-500">
                          Users must spend at least this many tokens before the
                          email fires.
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                      Trigger when they chatted with...
                    </span>
                    {hasCharacters ? (
                      <div className="flex flex-wrap gap-2">
                        {characters.map((character) => {
                          const isSelected = rule.triggerCharacterIds.includes(
                            character.id
                          );
                          return (
                            <button
                              key={`${rule.id}-${character.id}`}
                              type="button"
                              onClick={() =>
                                toggleTriggerCharacter(rule.id, character.id)
                              }
                              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                isSelected
                                  ? "border-[var(--primary-color)] bg-[var(--primary-color)] text-white shadow-sm"
                                  : "border-gray-200 bg-gray-100 text-gray-600 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                              }`}
                            >
                              {character.name}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <textarea
                        value={rule.triggerCharacterIds.join(", ")}
                        onChange={(event) =>
                          handleManualTriggerEntry(rule.id, event.target.value)
                        }
                        rows={2}
                        placeholder="Comma separated character IDs (e.g. aurora, cage)"
                        className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                      />
                    )}
                    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-600">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Or
                      </span>
                      <span>Trigger after any</span>
                      <input
                        type="number"
                        min={1}
                        inputMode="numeric"
                        placeholder="3"
                        value={triggerAfterAnyCountValue}
                        onChange={(event) =>
                          updateTriggerAfterAnyCount(rule.id, event.target.value)
                        }
                        className="w-20 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-center text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                      />
                      <span>characters they've chatted with.</span>
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-gray-500">
                      <span>{triggerSummary}</span>
                      <span>
                        You can only use one trigger type at a time; selecting
                        characters clears the “any characters” value and vice
                        versa.
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                      When should they reach out?
                    </span>

                    <div className="grid grid-cols-1 gap-3 rounded-2xl border border-white bg-white p-3 shadow-sm md:grid-cols-[1fr,1fr,1fr]">
                      <label className="flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Wait
                        </span>
                        <input
                          type="number"
                          min={0}
                          value={resolvedWindow.offsetValue}
                          onChange={(event) =>
                            updateSendWindow(rule.id, {
                              offsetValue: Number(event.target.value),
                            })
                          }
                          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                        />
                      </label>

                      <label className="flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Units
                        </span>
                        <select
                          value={resolvedWindow.offsetUnit}
                          onChange={(event) =>
                            updateSendWindow(rule.id, {
                              offsetUnit: event.target
                                .value as SendSchedule["offsetUnit"],
                            })
                          }
                          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                        >
                          {OFFSET_UNITS.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit.charAt(0).toUpperCase() + unit.slice(1)}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Send time anchor
                        </span>
                        <select
                          value={
                            resolvedWindow.sendTimeOptionId ??
                            DEFAULT_SEND_TIME_VALUE
                          }
                          onChange={(event) =>
                            updateSendWindow(rule.id, {
                              sendTimeOptionId:
                                event.target.value,
                            })
                          }
                          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                        >
                          <option value={DEFAULT_SEND_TIME_VALUE}>
                            Use default timing
                          </option>
                          {sendTimeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="inline-flex rounded-full border border-gray-200 bg-gray-100 p-1 text-xs font-semibold text-gray-600">
                      {(["prompt", "manual"] as const).map((modeOption) => (
                        <button
                          key={modeOption}
                          type="button"
                          onClick={() => setRuleContentMode(rule.id, modeOption)}
                          className={`rounded-full px-4 py-1 transition ${
                            activeContentMode === modeOption
                              ? "bg-white text-[var(--primary-color)] shadow"
                              : "text-gray-500"
                          }`}
                        >
                          {modeOption === "prompt"
                            ? "Prompt instructions"
                            : "Manual email"}
                        </button>
                      ))}
                    </div>

                    {activeContentMode === "prompt" ? (
                      <label className="flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Prompt instructions
                        </span>
                        <textarea
                          value={rule.promptInstructions ?? ""}
                          onChange={(event) =>
                            updateRule(rule.id, (current) => ({
                              ...current,
                              promptInstructions: event.target.value,
                            }))
                          }
                          rows={4}
                          placeholder="Outline how this character should follow up when this rule is matched."
                          className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                        />
                        <span className="text-xs text-gray-500">
                          Leave this blank if you plan to send a fully written email instead.
                        </span>
                        <span className="text-xs text-gray-500">
                          You can reference {"{{chatHistory}}"} to pull in the user&apos;s past conversation with this character.
                        </span>
                      </label>
                    ) : (
                      <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
                        <label className="flex flex-col gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                            Email subject
                          </span>
                          <input
                            type="text"
                            value={rule.manualEmailSubject ?? ""}
                            onChange={(event) =>
                              updateRule(rule.id, (current) => ({
                                ...current,
                                manualEmailSubject: event.target.value,
                              }))
                            }
                            placeholder="e.g., Aurora has a new update for you"
                            className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                          />
                        </label>
                        <EmailBodyEditor
                          value={rule.manualEmailBody ?? ""}
                          onChange={(nextValue) =>
                            updateRule(rule.id, (current) => ({
                              ...current,
                              manualEmailBody: nextValue,
                            }))
                          }
                          label="Email body"
                          helperText="If prompt instructions are empty, this email will send exactly as written."
                        />
                      </div>
                    ) }
                  </div>

                  <div className="flex items-center justify-end text-xs text-gray-500">
                    <button
                      type="button"
                      onClick={() => removeRule(rule.id)}
                      className="font-semibold text-red-500 hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={addRule}
        className="inline-flex items-center justify-center rounded-xl border border-dashed border-gray-300 px-4 py-2.5 text-sm font-semibold text-[var(--primary-color)] transition hover:border-[var(--primary-color)] hover:bg-[var(--primary-color)]/10"
      >
        Add reach-out rule
      </button>
    </div>
  );
};

export const PremiumConfigurationSection: React.FC<
  PremiumConfigurationSectionProps
> = ({
  criteria,
  prompt,
  characters,
  onUpdate,
  onUpdatePrompt,
  sendTimeOptions,
  onSave,
  isSaving,
}) => {
  const updateCriteria = (
    updater: (current: PremiumCriteria) => PremiumCriteria
  ) => {
    onUpdate(updater(criteria));
  };

  const rules = useMemo(
    () => criteria.characterRules ?? [],
    [criteria.characterRules]
  );

  return (
    <SectionCard
      title="Reach-out Character Rules"
      description="Orchestrate which character – free, patron, or premium – should email someone based on their chat history."
      collapsible
      defaultOpen={false}
    >
      <div className="mt-2">
        <ReachoutRulesEditor
          rules={rules}
          onChange={(nextRules: PremiumCharacterRule[]) =>
            updateCriteria((current) => ({
              ...current,
              characterRules: nextRules,
            }))
          }
          characters={characters}
          sendTimeOptions={sendTimeOptions}
        />
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="rounded-xl bg-[var(--primary-color)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-60"
          >
            {isSaving ? "Saving..." : "Save reach-out rules"}
          </button>
        </div>
      </div>
    </SectionCard>
  );
};

export default PremiumConfigurationSection;
